import { useEffect, useState } from "react"
import type { LegalDocumentKind } from "@ezbatna/core/domain/models"
import { getContainer } from "../lib/container"
import { Button, Card, CardBody, CardFooter, CardHeader, Spinner, Textarea, useToast } from "../components/ui"

const documents: { kind: LegalDocumentKind; title: string; description: string }[] = [
  {
    kind: "privacy_policy",
    title: "سياسة الخصوصية",
    description: "النص الظاهر للمستخدمين في صفحة سياسة الخصوصية داخل التطبيق والموقع.",
  },
  {
    kind: "terms_of_service",
    title: "شروط الاستخدام",
    description: "النص الظاهر للمستخدمين في صفحة شروط الاستخدام داخل التطبيق والموقع.",
  },
  {
    kind: "about_us",
    title: "من نحن",
    description: "النص الظاهر للمستخدمين في صفحة \"من نحن\" داخل الموقع.",
  },
]

function DocumentEditor({ kind, title, description }: { kind: LegalDocumentKind; title: string; description: string }) {
  const [content, setContent] = useState("")
  const [lastUpdated, setLastUpdated] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getContainer()
      .settingsRepository.fetchLegalDocument(kind)
      .then((doc) => {
        if (cancelled) return
        setContent(doc.content)
        setLastUpdated(doc.lastUpdated)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [kind])

  const handleSave = async () => {
    setSaving(true)
    try {
      await getContainer().settingsRepository.updateLegalDocument(kind, content)
      setLastUpdated(new Date().toISOString().slice(0, 10))
      toast.show(`تم حفظ ${title}`, "success")
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-title font-bold text-text-primary">{title}</h2>
        <p className="mt-2xs text-body-sm text-text-secondary">{description}</p>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex items-center justify-center py-2xl">
            <Spinner />
          </div>
        ) : (
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={14}
            className="min-h-72 font-mono text-body-sm"
            placeholder="اكتب المحتوى هنا (يدعم Markdown)…"
          />
        )}
      </CardBody>
      <CardFooter className="flex items-center justify-between gap-md">
        <p className="text-caption text-text-secondary">
          {lastUpdated ? `آخر تحديث: ${lastUpdated}` : ""}
        </p>
        <Button onClick={handleSave} loading={saving} disabled={loading}>
          حفظ
        </Button>
      </CardFooter>
    </Card>
  )
}

export function ContentEditorPage() {
  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-display font-bold text-text-primary">إدارة المحتوى</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">
          تعديل سياسة الخصوصية وشروط الاستخدام و"من نحن" — يُنشر التعديل مباشرة فور الحفظ.
        </p>
      </div>

      <div className="flex flex-col gap-lg">
        {documents.map((doc) => (
          <DocumentEditor key={doc.kind} {...doc} />
        ))}
      </div>
    </div>
  )
}
